/* eslint-disable max-len */
import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {Player} from '@lottiefiles/react-lottie-player';
import {
    notWebMediaQuery,
    PageBodyWrapper,
    mobileMediaQuery,
    tabletMediaQuery,
    tabletPortraitMediaQuery,
    useIsMobile,
    notMobileMediaQuery,
} from '../styles';

import CartoonitoLiveContainer from '../components/CartoonitoLiveContainer';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {selectHolidayTheme} from '../store/reducers';

const CartoonitoLive = props => {
    const {t} = useTranslation();
    const isMobile = useIsMobile();
    const holidayTheme = useSelector(selectHolidayTheme);
    return (
        <>
            <Helmet>
                <title>{t('cartoonito.videos.metaTitle')}</title>
                <meta
                    name="description"
                    content={t('cartoonito.videos.metaDescription')}
                />
            </Helmet>
            <BlueBackground>
                <WorldBG theme={holidayTheme} />
                <Container>
                    <BackgroundContainer theme={holidayTheme}>
                        <Itty />
                    </BackgroundContainer>
                    <CartoonitoLiveContainer />
                </Container>
            </BlueBackground>
        </>
    );
};

const Container = styled(PageBodyWrapper)`
    display: flex;
    width: 100%;
    align-self: stretch;
    flex: 1;
    padding: 56px 0;
    height: 55vw;
    max-width: 100%;
    max-height: 800px;

    ${mobileMediaQuery} {
        min-height: 480px;
    }

    ${notWebMediaQuery} {
        padding-top: 16px;
        margin-bottom: 100px;
    }

    ${tabletMediaQuery} {
        height: 500px;
    }
`;

const BlueBackground = styled.div`
    position: relative;
    align-self: stretch;
    flex: 1;
    background-color: #0066ff;
    background-image: url(${require('../assets/cnito_livestream_stars.png')});
    background-position: center;
    overflow: hidden;

    & > ${Container} {
        z-index: 1;
    }

    ${mobileMediaQuery} {
        background-size: 645px 393px;
        height: 300px;
    }
`;

const BackgroundContainer = styled.span`
    position: absolute;
    background-image: url(${({theme}) =>
        theme === 'xmas'
            ? require('../assets/cnito/cnito_live_bg_web_xmas.svg')
            : require('../assets/cnito_live_bg_web.svg')});
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center top;

    top: 460px;
    width: 100%;
    height: calc(100vw * 430 / 1348);
    align-self: center;

    ${tabletPortraitMediaQuery} {
        width: 768px;
        height: 420px;
        background-position: center;
        left: calc(50% - 384px);
        background-image: url(${({theme}) =>
            theme === 'xmas'
                ? require('../assets/cnito/cnito_live_bg_tablet_xmas.svg')
                : require('../assets/cnito_live_bg_tablet.svg')});
        top: 350px;
    }

    ${mobileMediaQuery} {
        width: 100%;
        height: calc(max(100vw, 375px) * 250 / 375);
        background-position: center;
        top: 280px;
        background-image: url(${({theme}) =>
            theme === 'xmas'
                ? require('../assets/cnito/cnito_live_bg_mobile_xmas.svg')
                : require('../assets/cnito_live_bg_mobile.svg')});
        min-width: 375px;
    }
`;

const WorldBG = styled.div`
    position: absolute;
    width: 100%;
    height: 250px;
    background-color: ${({theme}) => (theme === 'xmas' ? 'white' : '#33cef2')};
    bottom: 0;

    ${notMobileMediaQuery} {
        height: 120px;
    }
`;

export default CartoonitoLive;

const Itty = () => {
    const [isIdle, setIsIdle] = useState(false);
    const [isConfettiVisible, setIsConfettiVisible] = useState(true);
    const ref = useRef();
    const isMobile = useIsMobile();
    const onEvent = event => {
        if (event === 'complete') {
            setIsIdle(true);
        } else if (event === 'load' && isIdle) {
            // eslint-disable-next-line no-unused-expressions
            ref.current?.play();
        }
    };

    useEffect(() => {
        let timeout = setTimeout(() => {
            // eslint-disable-next-line no-unused-expressions
            ref.current?.play();
        }, 5000);
        let confettiTimeout = setTimeout(() => {
            setIsConfettiVisible(false);
        }, 5500);

        return () => {
            clearTimeout(timeout);
            clearTimeout(confettiTimeout);
        };
    }, []);

    return (
        <IttyContainer isIdle={isIdle}>
            <Player
                ref={ref}
                src={
                    isMobile
                        ? isIdle
                            ? require('../assets/animations/live_itty_idle_375.json')
                            : require('../assets/animations/live_itty_jump_375.json')
                        : isIdle
                        ? require('../assets/animations/live_itty_idle_768_1440.json')
                        : require('../assets/animations/live_itty_jump_768_1440.json')
                }
                autoplay={false}
                onEvent={onEvent}
                loop={isIdle}
            />
            {isConfettiVisible && (
                <Confetti>
                    <Player
                        src={require('../assets/animations/anim_confetti.json')}
                        autoplay={true}
                        loop={true}
                        style={{
                            position: 'absolute',
                            bottom: '-100px',
                            width: '100%',
                            height: '100%',
                        }}
                    />
                </Confetti>
            )}
        </IttyContainer>
    );
};

const IttyContainer = styled.div`
    position: absolute;
    top: -100px
    left: calc(50% - 700px);
    width: 360px;

    ${tabletMediaQuery} {
        left: calc(50% - 360px);
        bottom: unset;
        top: -100px;
        width: 180px;
    }

    ${tabletPortraitMediaQuery} {
        left: calc(50% - 360px);
        bottom: unset;
        top: -40px;
        width: 220px;
    }

    ${mobileMediaQuery} {
        left: calc(50% - 180px);
        top: 80px;
        width: 225px;
        z-index: 2;
    }
`;

const Confetti = styled.div`
    position: absolute;
    bottom: 0px;
    width: 100%;
    height: 100%;

    ${tabletMediaQuery} {
        bottom: 50px;
    }

    ${mobileMediaQuery} {
        bottom: 60px;
        width: 90%;
    }
`;
